import styled from 'styled-components';
import * as S from '../../Dashboard/Dashboard.styles';

const MOBILE = '768px';
/** PortalNavbar 是 position:fixed / height:56px / z-index:1000 */
const NAVBAR_H = '56px';

/*
 * 移动端布局修复背景：
 * TestManagement 原本是纯桌面布局（整页没有任何 media query），而
 * Dashboard.styles.Button 在 <=768px 时强制 `width: 100%`。本页的操作按钮全是紧凑型
 * （内联 padding:4px 8px + whiteSpace:nowrap），在手机上被撑成整行宽度，把不换行的
 * flex 行挤出视口，再被 ListItem 的 `overflow: hidden` 裁掉——前台在手机上因此看不到
 * "Generate Report"，只剩最左边的 Edit / No Group Report。
 *
 * 下面这些组件在 <=768px 时：按钮恢复内容宽度并允许换行、左右两栏改为上下堆叠、
 * 筛选行允许换行，保证任何操作在窄屏下都可见可点。桌面端样式与原内联样式等价。
 */

/** 页面根容器 */
export const Page = styled(S.DashboardContainer)`
  @media (max-width: ${MOBILE}) {
    padding: 0.75rem;
    overflow-x: hidden;

    h1 {
      font-size: 1.5rem;
      padding-bottom: 0.5rem;
      margin-bottom: 0.75rem;
    }

    h2 {
      font-size: 1.15rem;
      margin: 0;
      padding-left: 0.6rem;
      border-left-width: 3px;
    }

    ${S.Section} {
      padding: 1rem 0.85rem;
      margin-bottom: 1rem;
      border-radius: 10px;

      /* hover 位移在触屏上会一直保持，且 transform 会让 position:fixed 子元素失效 */
      &:hover {
        transform: none;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      }
    }

    ${S.FormContainer} {
      padding: 0.85rem;
      margin-bottom: 1rem;
    }
  }
`;

/** 可换行的按钮行；手机端按钮保持内容宽度、长标签折行 */
export const ActionRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: ${MOBILE}) {
    button {
      /* 覆盖 Dashboard.styles.Button 的 width:100% */
      width: auto;
      /* 覆盖各按钮的内联 whiteSpace:'nowrap'，否则窄屏仍会超出容器 */
      white-space: normal !important;
      max-width: 100%;
      text-align: center;
    }
  }
`;

/** 筛选条（Campus / Term / Show Archived 等）：手机端允许换行，select 自适应 */
export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE}) {
    gap: 8px;

    select {
      flex: 1 1 150px;
      min-width: 0;
      max-width: 100%;
    }

    button {
      width: auto;
    }
  }
`;

/** Final Exam / Mock Test 顶部标签栏 */
export const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;

  @media (max-width: ${MOBILE}) {
    margin-bottom: 12px;

    button {
      flex: 1;
      padding: 10px 8px;
      font-size: 14px;
    }
  }
`;

/** 可折叠区块的标题行（标题 + 右侧 +/− 开关） */
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 15px;

  @media (max-width: ${MOBILE}) {
    flex-wrap: wrap;
    padding: 6px 0;
    margin-bottom: 12px;
  }
`;

/** 通用列表行（模板列表 / 学生 response 列表）：手机端上下堆叠 */
export const ListRow = styled(S.ListItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: ${MOBILE}) {
    flex-direction: column;
    align-items: stretch;
    padding: 0.85rem 1rem;

    &:hover {
      transform: none;
    }
  }
`;

/** 列表行左侧文字区：min-width:0 防止长文本把右侧按钮挤出行外 */
export const RowMeta = styled.div`
  min-width: 0;
  word-break: break-word;
`;

/** 列表行右侧按钮区 */
export const RowActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: ${MOBILE}) {
    width: 100%;
  }
`;

/*
 * Group 级别的黄色标题条。
 * 子元素顺序固定为：[标题] [按钮区] [+/− 开关]
 * 桌面：一行三栏；手机：标题与开关同处第一行，按钮区整行铺在第二行。
 */
export const GroupHeader = styled.div`
  background: #fff3cd;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ffeaa7;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  @media (max-width: ${MOBILE}) {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
    padding: 10px;

    > *:nth-child(1) {
      grid-row: 1;
      grid-column: 1;
    }
    > *:nth-child(3) {
      grid-row: 1;
      grid-column: 2;
    }
    > *:nth-child(2) {
      grid-row: 2;
      grid-column: 1 / -1;
    }
  }
`;

/** Group 标题条中间的按钮区（含 Generate Report for {group}） */
export const GroupActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: ${MOBILE}) {
    flex-wrap: wrap;
    margin-top: 8px;

    button {
      width: auto;
      white-space: normal !important;
      max-width: 100%;
      text-align: center;
    }
  }
`;

/** Modal 内的「输入框 + 提交按钮」行：手机端改为上下堆叠，按钮整行 */
export const ModalFormRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;

  @media (max-width: ${MOBILE}) {
    flex-direction: column;

    button {
      width: 100%;
      white-space: normal !important;
    }
  }
`;

/**
 * Report Queue 侧滑面板。
 * 原先 top:0 / z-index:999，被 z-index:1000 的固定 Navbar 盖住了顶部 56px，
 * 面板标题和右上角关闭按钮点不到（桌面端同样存在）。改为从 Navbar 下方开始。
 */
export const QueuePanel = styled.div`
  position: fixed;
  top: ${NAVBAR_H};
  right: 0;
  width: min(400px, 100vw);
  height: calc(100vh - ${NAVBAR_H});
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/**
 * Report Queue 悬浮入口。
 * 桌面保持右侧竖排标签；手机改为右下角横向胶囊，避免盖住列表按钮。
 */
export const QueueTab = styled.div`
  position: fixed;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  z-index: 1000;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 12px 0 0 12px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  font-size: 14px;
  font-weight: 600;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  border: none;

  @media (max-width: ${MOBILE}) {
    top: auto;
    bottom: 16px;
    right: 12px;
    transform: none;
    writing-mode: horizontal-tb;
    border-radius: 999px;
    padding: 10px 16px;
    font-size: 13px;
  }
`;
